import { useState, useEffect, useCallback } from 'react';
import { Calculator, Plus, Download, Trash2, X, Loader2, Pencil, Check, CheckCircle2, MessageSquare } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { authFetch, downloadFile } from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL;

const EstimateDetail = ({ est, isApproved, rates, onBack, onDelete, onExportPdf, onUpdate }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editName, setEditName] = useState(est.estimate_name);
  const [editNotes, setEditNotes] = useState(est.notes || '');
  const [editItems, setEditItems] = useState(
    (est.line_items || []).map(i => ({ ...i, hours: String(i.hours || ''), rate: String(i.rate || '') }))
  );

  const updateItem = (idx, field, val) => {
    setEditItems(prev => prev.map((item, i) => i === idx ? { ...item, [field]: val } : item));
  };
  const addRow = () => setEditItems(prev => [...prev, { role: '', hours: '', rate: '', description: '' }]);
  const removeRow = (idx) => setEditItems(prev => prev.filter((_, i) => i !== idx));

  const editTotalHours = editItems.reduce((s, i) => s + (parseFloat(i.hours) || 0), 0);
  const editTotalCost = editItems.reduce((s, i) => s + (parseFloat(i.hours) || 0) * (parseFloat(i.rate) || 0), 0);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await authFetch(`${API}/api/estimates/${est.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          estimate_name: editName,
          notes: editNotes,
          line_items: editItems.map(i => ({
            role: i.role, description: i.description,
            hours: parseFloat(i.hours) || 0,
            rate: parseFloat(i.rate) || 0,
          })),
        }),
      });
      if (!res.ok) throw new Error('Update failed');
      const data = await res.json();
      onUpdate(data.estimate);
      setEditing(false);
      toast.success('Estimate updated');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  const handleApprove = async () => {
    try {
      const res = await authFetch(`${API}/api/estimates/${est.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status: 'Approved' }),
      });
      if (!res.ok) throw new Error('Approve failed');
      const data = await res.json();
      onUpdate(data.estimate);
      toast.success('Estimate approved');
    } catch (e) { toast.error(e.message); }
  };

  const items = editing ? editItems : (est.line_items || []);
  const totalHours = editing ? editTotalHours : (est.total_hours || 0);
  const totalCost = editing ? editTotalCost : (est.total_cost || 0);

  return (
    <div data-testid="estimate-detail">
      <button onClick={onBack} className="flex items-center gap-1 text-sm text-brand hover:underline mb-4" data-testid="estimate-back-btn">&larr; Back</button>
      <div className="flex items-center justify-between mb-6">
        <div>
          {editing ? (
            <Input value={editName} onChange={(e) => setEditName(e.target.value)} className="text-xl font-semibold mb-1 max-w-md" data-testid="estimate-edit-name" />
          ) : (
            <h2 className="text-2xl font-semibold tracking-tight text-text-primary">{est.estimate_name}</h2>
          )}
          <p className="text-sm text-text-tertiary">
            {est.client_name} &middot;{' '}
            <span className={`inline-flex items-center gap-1 font-medium ${isApproved ? 'text-emerald-600' : 'text-amber-600'}`}>
              {isApproved ? <><CheckCircle2 size={12} /> Approved</> : est.status}
            </span>
          </p>
        </div>
        <div className="flex gap-2">
          {!isApproved && !editing && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="estimate-edit-btn">
              <Pencil size={14} className="mr-1" /> Edit
            </Button>
          )}
          {editing && (
            <>
              <Button variant="outline" size="sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
              <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={handleSave} disabled={saving} data-testid="estimate-save-btn">
                {saving ? <Loader2 size={14} className="animate-spin mr-1" /> : <Check size={14} className="mr-1" />} Save
              </Button>
            </>
          )}
          {!isApproved && !editing && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleApprove} data-testid="estimate-approve-btn">
              <CheckCircle2 size={14} className="mr-1" /> Approve
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={() => onExportPdf(est.id)} data-testid="estimate-export-pdf"><Download size={14} className="mr-1" /> PDF</Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              const url = `${window.location.origin}/internal/estimates/${est.id}`;
              const prefill = `Sharing this estimate — please review:\n*${est.estimate_name}* — ${est.client_name}\n${url}`;
              window.dispatchEvent(new CustomEvent('ventas:slack:dm', {
                detail: { prefillText: prefill },
              }));
            }}
            className="text-chart-purple border-chart-purple/30 hover:bg-chart-purple/10"
            data-testid="estimate-share-slack"
          >
            <MessageSquare size={14} className="mr-1" /> Share
          </Button>
          {!isApproved && <Button variant="outline" size="sm" className="text-red-500" onClick={() => onDelete(est.id)} data-testid="estimate-delete-btn"><Trash2 size={14} /></Button>}
        </div>
      </div>
      <Card>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#003d4a] text-white">
                <th className="p-3 text-left text-xs uppercase">Role</th>
                <th className="p-3 text-left text-xs uppercase">Description</th>
                <th className="p-3 text-right text-xs uppercase">Hours</th>
                <th className="p-3 text-right text-xs uppercase">Rate</th>
                <th className="p-3 text-right text-xs uppercase">Subtotal</th>
                {editing && <th className="p-3 w-10"></th>}
              </tr>
            </thead>
            <tbody>
              {items.map((item, i) => (
                <tr key={`item-${i}`} className={i % 2 === 0 ? 'bg-white' : 'bg-surface-page'}>
                  {editing ? (
                    <>
                      <td className="p-2"><Input value={item.role} onChange={(e) => updateItem(i, 'role', e.target.value)} className="h-8 text-sm" data-testid={`edit-role-${i}`} /></td>
                      <td className="p-2"><Input value={item.description} onChange={(e) => updateItem(i, 'description', e.target.value)} className="h-8 text-sm" data-testid={`edit-desc-${i}`} /></td>
                      <td className="p-2"><Input type="number" value={item.hours} onChange={(e) => updateItem(i, 'hours', e.target.value)} className="h-8 text-sm text-right w-20 ml-auto" data-testid={`edit-hours-${i}`} /></td>
                      <td className="p-2"><Input type="number" value={item.rate} onChange={(e) => updateItem(i, 'rate', e.target.value)} className="h-8 text-sm text-right w-24 ml-auto" data-testid={`edit-rate-${i}`} /></td>
                      <td className="p-3 text-right font-medium">${((parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                      <td className="p-2 text-center">
                        {items.length > 1 && (
                          <button onClick={() => removeRow(i)} className="text-red-400 hover:text-red-600 p-1" data-testid={`remove-row-${i}`}><X size={14} /></button>
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="p-3 font-medium">{item.role}</td>
                      <td className="p-3 text-text-secondary">{item.description}</td>
                      <td className="p-3 text-right">{parseFloat(item.hours).toFixed(1)}</td>
                      <td className="p-3 text-right">${parseFloat(item.rate).toLocaleString()}</td>
                      <td className="p-3 text-right font-medium">${(parseFloat(item.hours) * parseFloat(item.rate)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                    </>
                  )}
                </tr>
              ))}
              {editing && (
                <tr>
                  <td colSpan="6" className="p-2">
                    <button onClick={addRow} className="text-xs font-medium text-brand hover:text-brand-hover flex items-center gap-1" data-testid="estimate-add-row">
                      <Plus size={12} /> Add line item
                    </button>
                  </td>
                </tr>
              )}
              <tr className="bg-[#003d4a] text-white font-bold">
                <td className="p-3" colSpan="2">TOTAL</td>
                <td className="p-3 text-right">{totalHours.toFixed(1)}</td>
                <td className="p-3"></td>
                <td className="p-3 text-right">${totalCost.toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                {editing && <td></td>}
              </tr>
            </tbody>
          </table>
        </CardContent>
      </Card>
      {editing ? (
        <Card className="mt-4">
          <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Notes</CardTitle></CardHeader>
          <CardContent>
            <textarea
              value={editNotes}
              onChange={(e) => setEditNotes(e.target.value)}
              className="w-full text-sm border rounded-md p-3 min-h-[80px] focus:outline-none focus:ring-2 focus:ring-brand/30"
              placeholder="Add notes..."
              data-testid="estimate-edit-notes"
            />
          </CardContent>
        </Card>
      ) : (
        est.notes && (
          <Card className="mt-4">
            <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold uppercase tracking-wider text-text-tertiary">Notes</CardTitle></CardHeader>
            <CardContent><p className="text-sm text-text-secondary">{est.notes}</p></CardContent>
          </Card>
        )
      )}
    </div>
  );
};

const EstimatorsView = ({ user }) => {
  const [estimates, setEstimates] = useState([]);
  const [rates, setRates] = useState({});
  const [rateSource, setRateSource] = useState('built_in');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [clients, setClients] = useState([]);

  const [form, setForm] = useState({
    client_name: '', estimate_name: '', notes: '',
    line_items: [{ role: '', hours: '', rate: '', description: '' }],
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [estRes, ratesRes, driveRes] = await Promise.all([
        authFetch(`${API}/api/estimates`),
        authFetch(`${API}/api/estimates/rates`),
        // /api/drive/clients is scoped by the user's `assigned_folders` (Admin sees all)
        authFetch(`${API}/api/drive/clients`),
      ]);
      if (estRes.ok) setEstimates((await estRes.json()).estimates || []);
      if (ratesRes.ok) {
        const rd = await ratesRes.json();
        setRates(rd.rates || {});
        setRateSource(rd.source || 'built_in');
      }
      if (driveRes.ok) {
        const data = await driveRes.json();
        setClients((data.clients || []).map(c => ({
          id: c.drive_folder_id, name: c.folder_name, drive_folder_id: c.drive_folder_id,
        })));
      }
    } catch (e) { console.error('Failed to load estimates:', e); toast.error('Failed to load data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => setForm({ client_name: '', estimate_name: '', notes: '', line_items: [{ role: '', hours: '', rate: '', description: '' }] });

  const addLineItem = () => setForm(f => ({ ...f, line_items: [...f.line_items, { role: '', hours: '', rate: '', description: '' }] }));
  const removeLineItem = (i) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, idx) => idx !== i) }));
  const updateLineItem = (i, key, val) => {
    const copy = [...form.line_items];
    copy[i] = { ...copy[i], [key]: val };
    // Auto-fill rate from standard rates
    if (key === 'role' && rates[val]) copy[i].rate = String(rates[val]);
    setForm(f => ({ ...f, line_items: copy }));
  };

  const calcTotal = (items) => items.reduce((sum, i) => sum + (parseFloat(i.hours) || 0) * (parseFloat(i.rate) || 0), 0);
  const calcHours = (items) => items.reduce((sum, i) => sum + (parseFloat(i.hours) || 0), 0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.client_name || !form.estimate_name) { toast.error('Client and estimate name required'); return; }

    const payload = {
      ...form,
      line_items: form.line_items.map(i => ({ ...i, hours: parseFloat(i.hours) || 0, rate: parseFloat(i.rate) || 0 }))
    };
    const res = await authFetch(`${API}/api/estimates`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) });
    if (res.ok) { toast.success('Estimate created'); setShowForm(false); resetForm(); fetchData(); }
    else toast.error('Failed to create estimate');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this estimate?')) return;
    const res = await authFetch(`${API}/api/estimates/${id}`, { method: 'DELETE' });
    if (res.ok) { toast.success('Estimate deleted'); setSelectedEstimate(null); fetchData(); }
  };

  const exportPdf = async (id) => {
    try {
      await downloadFile(`/api/estimates/${id}/export-pdf`, `Estimate_${id.slice(0, 8)}.pdf`);
      toast.success('PDF downloaded');
    } catch (e) {
      toast.error(e.message);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-brand" size={28} /></div>;

  if (selectedEstimate) {
    const est = selectedEstimate;
    const isApproved = est.status === 'Approved';
    return <EstimateDetail
      est={est}
      isApproved={isApproved}
      rates={rates}
      onBack={() => setSelectedEstimate(null)}
      onDelete={handleDelete}
      onExportPdf={exportPdf}
      onUpdate={(updated) => { setSelectedEstimate(updated); fetchData(); }}
    />;
  }

  return (
    <div data-testid="estimators-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-text-primary">Estimators</h1>
          <p className="text-sm text-text-tertiary">
            Project hours & cost estimates
            {rateSource === 'google_drive' && <span className="ml-2 text-xs font-medium text-brand bg-brand/10 px-2 py-0.5 rounded-full" data-testid="rate-source-drive">Rates from Google Sheet</span>}
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowForm(true); }} className="bg-brand hover:bg-brand-hover" data-testid="create-estimate-btn"><Plus size={16} className="mr-1" /> New Estimate</Button>
      </div>

      {estimates.length === 0 ? (
        <Card><CardContent className="py-12 text-center"><Calculator className="mx-auto mb-3 text-text-muted" size={40} /><p className="text-text-tertiary">No estimates yet</p></CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {estimates.map(est => (
            <Card key={est.id} className="cursor-pointer hover:border-brand/30 transition-colors" onClick={() => setSelectedEstimate(est)} data-testid={`estimate-card-${est.id?.slice(0,8)}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between mb-2"><Calculator size={18} className="text-brand" />
                  <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${est.status === 'Draft' ? 'bg-amber-100 text-amber-700' : 'bg-emerald-100 text-emerald-700'}`}>{est.status}</span>
                </div>
                <h3 className="text-base font-bold text-text-primary mt-2">{est.estimate_name}</h3>
                <p className="text-sm text-text-tertiary mt-1">{est.client_name}</p>
                <div className="flex items-center justify-between mt-3 pt-3 border-t border-border-subtle">
                  <span className="text-xs text-text-muted">{(est.total_hours || 0).toFixed(0)} hrs</span>
                  <span className="text-sm font-bold text-brand">${(est.total_cost || 0).toLocaleString()}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl m-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold text-text-primary">New Estimate</h2>
              <button onClick={() => setShowForm(false)} className="p-1 rounded-lg hover:bg-surface-page"><X size={20} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Client</Label>
                  <select value={form.client_name} onChange={e => setForm(f => ({ ...f, client_name: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white" data-testid="estimate-client-select" required>
                    <option value="">Select client...</option>
                    {clients.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                  </select>
                </div>
                <div><Label>Estimate Name</Label><Input value={form.estimate_name} onChange={e => setForm(f => ({ ...f, estimate_name: e.target.value }))} placeholder="e.g. Q2 Campaign Setup" className="mt-1.5" required data-testid="estimate-name-input" /></div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-2"><Label>Line Items</Label><button type="button" onClick={addLineItem} className="text-xs text-brand hover:underline">+ Add Row</button></div>
                <div className="border rounded-xl overflow-hidden">
                  <table className="w-full text-sm">
                    <thead><tr className="bg-surface-page"><th className="p-2 text-left text-xs">Role</th><th className="p-2 text-left text-xs">Description</th><th className="p-2 text-right text-xs w-20">Hours</th><th className="p-2 text-right text-xs w-24">Rate ($)</th><th className="p-2 text-right text-xs w-24">Subtotal</th><th className="w-8"></th></tr></thead>
                    <tbody>
                      {form.line_items.map((item, i) => (
                        <tr key={`item-${i}`} className="border-t">
                          <td className="p-1.5">
                            <select value={item.role} onChange={e => updateLineItem(i, 'role', e.target.value)} className="w-full px-2 py-1.5 border border-input rounded-lg bg-white text-sm" data-testid={`line-item-role-${i}`}>
                              <option value="">Select role...</option>
                              {Object.keys(rates).map(r => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </td>
                          <td className="p-1.5"><input value={item.description} onChange={e => updateLineItem(i, 'description', e.target.value)} className="w-full px-2 py-1.5 border border-input rounded-lg text-sm" placeholder="Task" /></td>
                          <td className="p-1.5"><input type="number" value={item.hours} onChange={e => updateLineItem(i, 'hours', e.target.value)} className="w-full px-2 py-1.5 border border-input rounded-lg text-sm text-right" placeholder="0" /></td>
                          <td className="p-1.5"><input type="number" value={item.rate} onChange={e => updateLineItem(i, 'rate', e.target.value)} className="w-full px-2 py-1.5 border border-input rounded-lg text-sm text-right" placeholder="0" /></td>
                          <td className="p-1.5 text-right text-sm font-medium">${((parseFloat(item.hours) || 0) * (parseFloat(item.rate) || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}</td>
                          <td className="p-1.5">{form.line_items.length > 1 && <button type="button" onClick={() => removeLineItem(i)} className="text-red-400 hover:text-red-600"><X size={14} /></button>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex justify-between items-center px-3 py-2 bg-[#003d4a] text-white text-sm font-bold">
                    <span>TOTAL</span>
                    <span>{calcHours(form.line_items).toFixed(1)} hrs &middot; ${calcTotal(form.line_items).toLocaleString(undefined, {minimumFractionDigits: 2})}</span>
                  </div>
                </div>
              </div>

              <div><Label>Notes</Label><textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="w-full mt-1.5 px-3 py-2 border border-input rounded-xl bg-white min-h-[60px] text-sm" /></div>
              <div className="flex justify-end gap-3 pt-2">
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button type="submit" className="bg-brand hover:bg-brand-hover" data-testid="estimate-submit">Create Estimate</Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default EstimatorsView;
